export const NEAR_ZERO_THRESHOLD = 1e-8;
export class VerticalAlignment {
    // ------------------------ Private Instance Properties ------------------------
    _verticalAlignmentData;
    _horizontalAlignment;
    _curvePoints;
    constructor(horizontalAlignment) {
        this._verticalAlignmentData = [];
        this._horizontalAlignment = horizontalAlignment;
        this._curvePoints = [];
    }
    // ------------------------ Public Getters/Setters ------------------------
    get verticalAlignmentData() {
        return this._verticalAlignmentData;
    }
    get curvePoints() {
        return this._curvePoints;
    }
    // ------------------------ Public Methods ------------------------
    dispose() {
        this._verticalAlignmentData.length = 0;
        this._curvePoints.length = 0;
    }
    getElevationForHorizontalDistance(horizontalDistance) {
        const verticalAlignmentData = this.verticalAlignmentData;
        if (verticalAlignmentData.length === 0) {
            return 0;
        }
        // If distance is beyond the last point, return the last point's elevation (flat extension)
        if (horizontalDistance >=
            verticalAlignmentData[verticalAlignmentData.length - 1].horizontalDistance) {
            return verticalAlignmentData[verticalAlignmentData.length - 1].elevation;
        }
        let elevation = 0;
        for (let i = 0; i < verticalAlignmentData.length; i++) {
            if (verticalAlignmentData[i].horizontalDistance >= horizontalDistance) {
                const previousElevation = i === 0 ? 0 : verticalAlignmentData[i - 1].elevation;
                const currentElevation = verticalAlignmentData[i].elevation;
                const distanceToPrevious = i === 0 ? 0 : verticalAlignmentData[i - 1].horizontalDistance;
                const distanceToCurrent = verticalAlignmentData[i].horizontalDistance;
                if (distanceToCurrent === distanceToPrevious) {
                    return currentElevation;
                }
                const slope = (currentElevation - previousElevation) /
                    (distanceToCurrent - distanceToPrevious);
                elevation =
                    slope * (horizontalDistance - distanceToPrevious) + previousElevation;
                break;
            }
        }
        return elevation;
    }
    //ToDo: Optimize this function
    getElevationForHorizontalDistanceInBatch(sortedHorizontalDistances) {
        const elevations = [];
        for (let i = 0; i < sortedHorizontalDistances.length; i++) {
            elevations.push(this.getElevationForHorizontalDistance(sortedHorizontalDistances[i]));
        }
        return elevations;
    }
    updateData(data) {
        this._verticalAlignmentData = data;
        if (this._verticalAlignmentData.length === 0) {
            this.initializeVerticalAlignmentData();
        }
        this.sortVerticalAlignmentData();
    }
    getHeightRange() {
        const info = {
            min: Number.POSITIVE_INFINITY,
            max: Number.NEGATIVE_INFINITY,
        };
        const heights = this._verticalAlignmentData.map((va) => {
            return va.elevation;
        });
        const minHeight = Math.min(...heights);
        const maxHeight = Math.max(...heights);
        if (info.min > minHeight) {
            info.min = minHeight;
        }
        if (info.max < maxHeight) {
            info.max = maxHeight;
        }
        return info;
    }
    getSlopeRange() {
        const info = {
            min: Number.POSITIVE_INFINITY,
            max: Number.NEGATIVE_INFINITY,
        };
        const verticalPathPoints = this.verticalAlignmentData.map((va) => {
            return [va.horizontalDistance, va.elevation];
        });
        const isNearZero = (v) => Math.abs(v) < NEAR_ZERO_THRESHOLD;
        const updateRange = (info, slope) => {
            info.min = Math.min(info.min, slope);
            info.max = Math.max(info.max, slope);
        };
        const slopeFromDelta = (dx, dy) => {
            if (!isNearZero(dx))
                return dy / dx;
            if (!isNearZero(dy))
                return dy > 0 ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
            return 0;
        };
        verticalPathPoints.forEach((currP, i) => {
            const nextP = verticalPathPoints[i + 1];
            if (nextP === undefined)
                return;
            const dx = nextP[0] - currP[0];
            const dy = nextP[1] - currP[1];
            const slope = slopeFromDelta(dx, dy);
            updateRange(info, slope);
        });
        return info;
    }
    // ------------------------ Private Methods ------------------------
    sortVerticalAlignmentData() {
        this._verticalAlignmentData.sort((a, b) => a.horizontalDistance - b.horizontalDistance);
    }
    initializeVerticalAlignmentData() {
        // set for 0 and last horizontal distance
        this._verticalAlignmentData.push({
            horizontalDistance: 0,
            elevation: this._horizontalAlignment.points[0].z, // get elevation from horizontal alignment
        });
        this._verticalAlignmentData.push({
            horizontalDistance: this._horizontalAlignment.length,
            elevation: this._horizontalAlignment.points[this._horizontalAlignment.points.length - 1].z, // get elevation from horizontal alignment
        });
    }
}
//# sourceMappingURL=vertical-alignment.js.map